import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchPastEvent, pastEventPdfUrl, type ConferencePastEvent } from "./api";

function localizedTitle(item: ConferencePastEvent, locale: string) {
  if (locale === "ar") return item.title_ar;
  if (locale === "fr") return item.title_fr;
  return item.title_en;
}

function localizedDescription(item: ConferencePastEvent, locale: string) {
  if (locale === "ar") return item.description_ar;
  if (locale === "fr") return item.description_fr;
  return item.description_en;
}

export function PastEventReaderPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { t, locale } = useLanguage();
  const [item, setItem] = useState<ConferencePastEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    fetchPastEvent(eventId)
      .then((row) => {
        if (!cancelled) setItem(row);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t.common.error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, t.common.error]);

  if (loading) {
    return <p className="dn-muted">{t.common.loading}</p>;
  }
  if (error || !item) {
    return (
      <div>
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
        <Link className="dn-btn dn-btn-ghost" to="/past-events">
          {t.pastEvents.backToArchive}
        </Link>
      </div>
    );
  }

  const title = localizedTitle(item, locale);
  const description = localizedDescription(item, locale);
  const pdfInline = pastEventPdfUrl(item.slug, false);
  const pdfDownload = pastEventPdfUrl(item.slug, true);

  return (
    <div className="dn-reader">
      <nav className="dn-reader-breadcrumb" aria-label="breadcrumb">
        <Link to="/past-events">{t.pastEvents.title}</Link>
        <span aria-hidden="true"> / </span>
        <span>{title}</span>
      </nav>

      <div className="dn-reader-header">
        <div>
          <h1>{title}</h1>
          {item.year != null && (
            <p className="dn-muted">
              {t.pastEvents.year}: {item.year}
            </p>
          )}
          {description && <p className="dn-muted">{description}</p>}
        </div>
        <div className="dn-reader-actions">
          {item.has_pdf && (
            <>
              <a className="dn-btn dn-btn-primary" href={pdfInline} target="_blank" rel="noreferrer">
                {t.pastEvents.openPdf}
              </a>
              <a className="dn-btn dn-btn-ghost" href={pdfDownload}>
                {t.pastEvents.downloadPdf}
              </a>
            </>
          )}
          <Link className="dn-btn dn-btn-ghost" to="/past-events">
            {t.pastEvents.backToArchive}
          </Link>
        </div>
      </div>

      {item.has_pdf ? (
        <div className="dn-reader-frame-wrap">
          <iframe className="dn-reader-frame" title={title} src={pdfInline} />
          <p className="dn-muted" style={{ marginTop: "0.75rem" }}>
            <a href={pdfInline} target="_blank" rel="noreferrer">
              {t.pastEvents.openFullscreen}
            </a>
          </p>
        </div>
      ) : (
        <div className="dn-card">
          <p>{t.pastEvents.pdfUnavailable}</p>
        </div>
      )}
    </div>
  );
}
